import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import api from 'src/services/api';
import { formatMoney } from 'src/utils/common';
import { useAlert } from 'src/hooks/useAlert';
import { Container } from 'src/components/Layouts/Container';
import { Centered } from 'src/components/Layouts/Container/styles';

import PagamentoService from '../services/Pagamento';

const formatDate = (date) => {
  if (!date || !date.includes('-')) return date;

  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

export function PagamentoComPix() {
  const history = useHistory();
  const location = useLocation();
  const usuario = useSelector((state) => state.user);
  const { alert, openAlert, closeAlert } = useAlert();

  const plano = location?.state?.plano;

  const [formSubmiting, setFormSubmiting] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [pixResponse, setPixResponse] = useState(null);
  const pixRequestedRef = useRef(false);

  const handleGerarPix = async () => {
    if (!plano) {
      openAlert('Serviço não encontrado para gerar o Pix.', 'error');
      return;
    }

    if (!usuario?.cpf) {
      openAlert(
        'É necessário ter um CPF cadastrado para gerar a cobrança Pix.',
        'warning'
      );
      return;
    }

    try {
      setFormSubmiting(true);

      const item = {
        description: `XMEI - ${plano.nome}`,
        quantity: 1,
        price_cents: Math.round(parseFloat(plano.valor) * 100),
      };

      const response = await PagamentoService.contratarServicoComPix({
        id: usuario.id,
        email: usuario.email,
        cpf_cnpj: usuario.cpf,
        username: usuario.nome,
        servicoId: plano.id,
        item,
      });

      setPixResponse(response.data);
    } catch (error) {
      openAlert(
        error?.response?.data?.message || 'Falha ao tentar gerar o Pix.',
        'error'
      );
    } finally {
      setFormSubmiting(false);
    }
  };

  const handleCopyPix = async () => {
    if (!pixResponse?.pix?.qrcode_text || !navigator?.clipboard) {
      return;
    }

    await navigator.clipboard.writeText(pixResponse.pix.qrcode_text);
    openAlert('Código Pix copiado com sucesso.', 'success');
  };

  const handleVerificarPagamento = async () => {
    if (!pixResponse?.fatura?.id) {
      return;
    }

    try {
      setCheckingPayment(true);
      const response = await api.get(`/buscarFatura/${pixResponse.fatura.id}`);
      const faturaAtualizada = response?.data?.fatura;

      setPixResponse((current) => ({
        ...current,
        fatura: faturaAtualizada,
        pix: faturaAtualizada?.pix || current?.pix,
      }));

      if (faturaAtualizada?.status === 'paid') {
        history.replace({
          pathname: `/servicosUsuario/detalhe/${plano.id}`,
          state: {
            message:
              'Pagamento Pix confirmado. Serviço contratado com sucesso.',
            servico: plano,
          },
        });
        return;
      }

      const { data: pixLocal } = await api.get(
        `/pixServicoConfirmado/${pixResponse.fatura.id}`
      );
      if (pixLocal?.confirmado) {
        history.replace({
          pathname: `/servicosUsuario/detalhe/${plano.id}`,
          state: {
            message:
              'Pagamento Pix confirmado. Serviço contratado com sucesso.',
            servico: plano,
          },
        });
        return;
      }

      if (['expired', 'canceled'].includes(faturaAtualizada?.status)) {
        openAlert(
          'A cobrança Pix expirou ou foi cancelada. Gere uma nova cobrança para continuar.',
          'warning'
        );
        return;
      }

      openAlert(
        'O pagamento ainda está pendente. Após pagar, clique em verificar novamente.',
        'info'
      );
    } catch (error) {
      openAlert(
        'Não foi possível verificar o status do pagamento agora.',
        'error'
      );
    } finally {
      setCheckingPayment(false);
    }
  };

  useEffect(() => {
    if (pixRequestedRef.current) return;
    if (!plano || !usuario?.cpf) return;

    pixRequestedRef.current = true;
    handleGerarPix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plano, usuario?.cpf]);

  return (
    <Container maxwidth={800}>
      <Collapse in={alert.open}>
        <Alert severity={alert.severity} onClick={closeAlert}>
          {alert.message}
        </Alert>
      </Collapse>

      <Box mb={3}>
        <Typography variant="h5">Pagamento via Pix</Typography>
        <Typography color="textSecondary">
          Gere o QR Code para pagar este serviço. A liberação acontece após a
          confirmação do pagamento pela IUGU.
        </Typography>
        {!usuario?.cpf && (
          <Alert severity="warning" style={{ marginTop: 16 }}>
            É necessário ter um CPF cadastrado para gerar a cobrança Pix.{' '}
            <Typography
              component="span"
              color="primary"
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => history.push('/usuarioDados')}
            >
              Adicionar CPF nos dados pessoais
            </Typography>
          </Alert>
        )}
      </Box>

      <Box mb={3}>
        <Typography variant="subtitle2">Serviço</Typography>
        <Typography variant="h6">{plano?.nome || '-'}</Typography>
      </Box>

      <Box mb={3}>
        <Typography variant="subtitle2">Valor</Typography>
        <Typography variant="h6">
          {plano ? formatMoney(Number(plano.valor)) : 'R$ 0,00'}
        </Typography>
      </Box>

      {!pixResponse && formSubmiting && (
        <Centered>
          <Box
            marginTop={2}
            display="flex"
            alignItems="center"
            style={{ gap: 12 }}
          >
            <CircularProgress color="primary" size={25} />
            <Typography color="textSecondary">Gerando Pix...</Typography>
          </Box>
        </Centered>
      )}

      {!pixResponse && !formSubmiting && usuario?.cpf && (
        <Centered>
          <Box marginTop={2}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={handleGerarPix}
            >
              Tentar novamente
            </Button>
          </Box>
        </Centered>
      )}

      {pixResponse?.pix && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Vencimento</Typography>
            <Typography>{formatDate(pixResponse?.fatura?.due_date)}</Typography>
          </Grid>

          {pixResponse.pix.qrcode && (
            <Grid item xs={12}>
              <img
                src={pixResponse.pix.qrcode}
                alt="QR Code Pix"
                style={{ maxWidth: 280, width: '100%' }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2">Pix copia e cola</Typography>
            <Box
              p={2}
              style={{ backgroundColor: '#F5F5F5', wordBreak: 'break-all' }}
            >
              <Typography>{pixResponse.pix.qrcode_text}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyPix}
              style={{ marginRight: 12 }}
            >
              Copiar código Pix
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleVerificarPagamento}
              disabled={checkingPayment}
            >
              {checkingPayment ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                'Verificar pagamento'
              )}
            </Button>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default PagamentoComPix;
